import type { GotTraceDetails } from "@/models/traces";
import type { TraceState } from "@/store/trace/state";
import type { RootState } from "@/store/types";
import apolloClient from "@/apollo";
import { gql } from "graphql-tag";
import getTraceSpans from "./getTraceSpans";
import type { ActionContext } from "vuex";

// The query is called getTrace, but we are changing the action name to "loadTrace" so it
// doesn't get confused with the getter "getTrace".
const getTraceQuery = gql`
  query GetTraceDetails($input: GetTraceDetails!) {
    getTraceDetails(input: $input) {
      __typename
      ... on GotTraceDetails {
        filenames
        trace {
          attributes {
            key
            value
          }
          elapsed
          ends
          id
          label
          language
          libraryName
          libraryVersion
          name
          resourceAttributes {
            key
            value
          }
          serviceInstanceId
          serviceName
          serviceNamespace
          serviceVersion
          starts
        }
      }
      ... on Corollary {
        code
        message
      }
    }
  }
`;

/**
 * Executes the query `getTrace` and assigns the Trace value.
 * @param context - Context where the action is being taking place.
 * @param args - Arguments object containing the required parameters.
 * @param args.filename - Encoded filename string of the current Trace.
 * @param args.traceId - Trace ID of the current Trace.
 * @returns It doesn't return anything on success, but if an error occurred, then it returns
 *    the error with the trace id.
 */
export async function loadTrace(
  context: ActionContext<TraceState, RootState>,
  args: { filename: string; traceId: string }
): Promise<undefined | { message: string; traceId: string }> {
  try {
    const result = await apolloClient.query({
      query: getTraceQuery,
      variables: {
        input: {
          filename: args.filename,
          traceId: args.traceId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const typeName = result.data.getTraceDetails.__typename;
    if (typeName === "Corollary") {
      return {
        message: result.data.getTraceDetails.message,
        traceId: args.traceId as string,
      };
    }
    const gotTraceDetails: GotTraceDetails = result.data.getTraceDetails;
    context.commit("setFilename", args.filename);
    context.commit("setTrace", gotTraceDetails.trace);

    const queries = gotTraceDetails.filenames.map((filename) =>
      getTraceSpans(context, args.traceId, filename)
    );
    const errors = (await Promise.all(queries)).filter((x) => x !== undefined);
    context.commit("setSpans", [...new Set(context.getters["getSpans"] || [])]);
    if (errors && errors.length > 0) {
      context.commit("setFilename", undefined);
      context.commit("setTrace", undefined);
      context.commit("setSpans", undefined);
      return errors[0];
    }
  } catch (e: any) {
    const error = e.graphQLErrors ? e.graphQLErrors[0] : e;
    return {
      message: error.message,
      traceId: args.traceId as string,
    };
  }
}
